import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, LogOut, LayoutDashboard, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar({ transparent = false }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();

  const dashboardPath = {
    plaza_admin: "/admin",
    tienda: "/tienda",
    repartidor: "/repartidor",
    cliente: "/cliente",
    neni: "/neni",
  }[user?.role] || "/";

  return (
    <header className={`sticky top-0 z-40 ${transparent ? "bg-white/70 backdrop-blur-xl" : "bg-white"} border-b border-zinc-200`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-sm bg-[#FF3366] grid place-items-center">
            <Store className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-xl tracking-tight">en 90<span className="text-[#FF3366]"> MINUTOS</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <Link to="/" className="hover:text-[#FF3366] transition-colors" data-testid="nav-home">Explorar</Link>
          <Link to="/plazas" className="hover:text-[#FF3366] transition-colors" data-testid="nav-plazas">Plazas</Link>
          <Link to="/para-locatarios" className="hover:text-[#FF3366] transition-colors" data-testid="nav-locatarios">Para negocios</Link>
          <Link to="/para-repartidores" className="hover:text-[#FF3366] transition-colors" data-testid="nav-repartidores">Repartidores</Link>
          <Link to="/nenis-place" className="hover:text-[#FF3366] transition-colors" data-testid="nav-nenis">NENIS PLACE</Link>
          <a href="#como-funciona" className="hover:text-[#FF3366] transition-colors">Cómo funciona</a>
        </nav>

        <div className="flex items-center gap-3">
          {user && <NotificationBell />}
          <Link to="/carrito" data-testid="nav-cart" className="relative p-2 hover:bg-zinc-100 rounded-sm transition-colors">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF3366] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center">
                {count}
              </span>
            )}
          </Link>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="nav-user-menu" className="rounded-sm border-zinc-300">
                  <User className="w-4 h-4 mr-1.5" />
                  <span className="max-w-[120px] truncate">{user.nombre}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-zinc-500">{user.email}</div>
                  <div className="text-xs mt-0.5 uppercase tracking-wider text-[#FF3366] font-semibold">{user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav(dashboardPath)} data-testid="nav-dashboard-link">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Mi Panel
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium hover:text-[#FF3366]">Entrar</Link>
              <Link to="/register" data-testid="nav-register">
                <Button size="sm" className="rounded-sm bg-[#FF3366] hover:bg-[#E62958] text-white">Crear cuenta</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
